// services/socket.js
import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.typingTimeout = null;
  }

  connect(token) {
    if (this.socket) {
      this.disconnect();
    }

    this.socket = io({
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      withCredentials: true
    });

    this.setupDefaultListeners();
    return this.socket;
  }

  setupDefaultListeners() {
    this.socket.on('connect', () => {
      console.log('✅ Socket connected');
      this.emit('socket:connected');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      this.emit('socket:disconnected', reason);
    });

    this.socket.on('connect_error', (err) => {
      console.error('❌ Socket connection error:', err);
      this.emit('socket:error', err);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Socket reconnected after', attemptNumber, 'attempts');
      this.emit('socket:reconnected', attemptNumber);
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Socket reconnect attempt:', attemptNumber);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('❌ Socket reconnect error:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ Socket reconnect failed');
      this.emit('socket:reconnect_failed');
    });

    // File attachment specific listeners
    this.socket.on('fileAttachmentReceived', (data) => {
      console.log('📎 File attachment received via socket:', data);
      this.emit('fileAttachmentReceived', data);
    });

    this.socket.on('fileDownloadReady', (data) => {
      console.log('📥 File download ready via socket:', data);
      this.emit('fileDownloadReady', data);
    });

    this.socket.on('fileDownloadError', (data) => {
      console.error('❌ File download error via socket:', data);
      this.emit('fileDownloadError', data);
    });

    this.socket.on('fileUploadProgress', (data) => {
      console.log('📤 File upload progress:', data);
      this.emit('fileUploadProgress', data);
    });
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
    
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  emit(event, data, callback) {
    if (this.socket && this.socket.connected) {
      if (callback) {
        this.socket.emit(event, data, callback);
      } else {
        this.socket.emit(event, data);
      }
    } else {
      console.warn(`⚠️ Socket not connected, cannot emit ${event}`);
      if (callback) {
        callback({ error: 'Socket not connected' });
      }
    }
  }

  // Message events
  sendMessage(messageData, callback) {
    this.emit('sendMessage', messageData, callback);
  }

  sendMessageWithFile(messageData, callback) {
    this.emit('sendMessageWithFile', messageData, callback);
  }

  markMessageDelivered(messageId) {
    this.emit('messageDelivered', { messageId });
  }

  markMessageRead(messageId) {
    this.emit('messageRead', { messageId });
  }

  // Typing indicator
  handleTyping(recipientUuid, isTyping = true) {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    
    this.emit('typing', { recipientUuid, isTyping });
    
    if (isTyping) {
      this.typingTimeout = setTimeout(() => {
        this.emit('typing', { recipientUuid, isTyping: false });
      }, 2000);
    }
  }

  // File attachment events
  requestFileDownload(fileId) {
    this.emit('requestFileDownload', { fileId });
  }

  notifyFileUploaded(fileId, messageId) {
    this.emit('fileUploaded', { fileId, messageId });
  }

  notifyFileDeleted(fileId) {
    this.emit('fileDeleted', { fileId });
  }

  // Connection status
  isConnected() {
    return this.socket && this.socket.connected;
  }

  getSocketId() {
    return this.socket ? this.socket.id : null;
  }

  // Disconnect and cleanup
  disconnect() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
    
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
  }

  // Reconnect manually
  reconnect() {
    if (this.socket) {
      this.socket.connect();
    }
  }
}

// Create singleton instance
const socketService = new SocketService();

// Add convenience methods for global event listening
socketService.onAny = (callback) => {
  if (socketService.socket) {
    socketService.socket.onAny(callback);
  }
};

socketService.offAny = (callback) => {
  if (socketService.socket) {
    socketService.socket.offAny(callback);
  }
};

export default socketService;